import * as Sentry from '@sentry/react';
import { assignVariant } from './experiment-assignment.ts';
import { EXPERIMENTS } from './homepage-experiments.ts';
import { getEnabledExperiments } from './config.ts';
import { sharedCookieDomain } from './cookie-domain.ts';
import { getVisitorId } from './visitor-id.ts';

/**
 * Homepage experiment engine: decide which experiments are enrolling, assign the
 * visitor a variant, and tag it in Sentry so it rides every span, event, replay,
 * and conversion the session emits.
 *
 * Enrollment is read from runtime config (the `EXPERIMENTS` list — see
 * `config.ts`), so it's known synchronously at load with no API call. Assignment
 * is a pure function of a first-party visitor id (`gp_vid`) and the experiment
 * key, so it's stable across reloads and computed before first paint — no flash.
 * `Home.tsx` reads the result through `getVariant()`. See docs/experimentation.md.
 */

// The resolved assignment map ({ experimentKey: variant }), mirrored into a
// registrable-domain cookie so the product app (app.) can read which variant the
// visitor saw — and attribute a signup to it — without re-deriving it from the
// registry. www writes; app. reads (webapp/src/experiments.ts). Mirrors the
// gp_li_fat_id bridge. See docs/experimentation.md.
const ASSIGNMENTS_COOKIE = 'gp_exp';
const ASSIGNMENTS_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

// Resolved once at boot, after init runs. A module singleton (not a context)
// because the assignment is fixed for the session and computed before render —
// `getVariant()` reads, it never recomputes.
let assignments: Record<string, string> = {};

/**
 * Assign and tag the visitor for every enrolling experiment. Call once at boot
 * from `main.tsx`, after `initSentry` so the scope exists. No-ops (and sets no
 * cookie) when nothing is enrolling. Best-effort: a storage failure must never
 * break the page.
 */
export function initExperiments(): void {
  const forced = forcedVariants();
  const enabled = new Set(getEnabledExperiments());
  // Process an experiment if it's enrolling OR force-overridden (preview), and
  // has variants to assign — skip a misconfigured empty one rather than let
  // assignment throw and break the page render.
  const active = EXPERIMENTS.filter(
    (experiment) => (enabled.has(experiment.key) || forced[experiment.key]) && experiment.variants.length > 0,
  );
  if (active.length === 0) return;

  // Mint the visitor id only now — once an experiment is enrolling (or being
  // previewed) — so a visitor who never meets one is never cookied.
  const vid = getVisitorId();
  const resolved: Record<string, string> = {};
  const attributes: Record<string, string> = {};
  for (const experiment of active) {
    // A forced override (from the URL) wins over the hashed assignment, so a
    // teammate can preview any variant; otherwise bucket deterministically.
    const variant = forced[experiment.key] ?? assignVariant(vid, experiment);
    resolved[experiment.key] = variant;
    // Individually filterable in Sentry (e.g.
    // `experiment.homepage_conversion_v1:treatment`). The tag rides every
    // error and the replay search for the session; the scope attribute rides
    // every span, log, and metric. Scope tags do not reach spans, so the
    // attribute is what makes the `gp.ui.*` spans sliceable by variant.
    Sentry.setTag(`experiment.${experiment.key}`, variant);
    attributes[`experiment.${experiment.key}`] = variant;
  }
  Sentry.setAttributes(attributes);
  assignments = resolved;
  // The grouped context keeps the full assignment set together on the event
  // detail for a quick read.
  Sentry.setContext('experiment', resolved);
  // Bridge the assignment to app. so a later signup can be attributed to it.
  writeAssignmentsCookie(resolved);
}

// Force-override the assigned variant via the URL, for previewing/QA — e.g.
// `?gp_exp=homepage_conversion_v1:treatment` (comma-separate to force several).
// An override activates its experiment for the session even when it isn't in
// `EXPERIMENTS`, and is validated against the catalog (unknown key or variant is
// ignored), so the param can only select a real variant of a real experiment.
const FORCE_PARAM = 'gp_exp';

function forcedVariants(): Record<string, string> {
  try {
    const raw = new URLSearchParams(window.location.search).get(FORCE_PARAM);
    if (!raw) return {};
    const out: Record<string, string> = {};
    for (const pair of raw.split(',')) {
      const [key, variant] = pair.split(':').map((s) => s.trim());
      if (!key || !variant) continue;
      const experiment = EXPERIMENTS.find((e) => e.key === key);
      if (experiment && experiment.variants.some((v) => v.key === variant)) {
        out[key] = variant;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * The variant assigned for an experiment this session, or null when it isn't
 * enrolling. A null result means "render the canonical content" — an unenrolled
 * session is deliberately distinct from an enrolled `control` one.
 *
 * This is the render seam for a variant (docs/experimentation.md): a page
 * gates a region on `getVariant(key)`. With no experiment defined right now it
 * has no caller, hence `@public` so the unused-export check keeps it.
 *
 * @public
 */
export function getVariant(experimentKey: string): string | null {
  return assignments[experimentKey] ?? null;
}

function writeAssignmentsCookie(resolved: Record<string, string>): void {
  try {
    const value = encodeURIComponent(JSON.stringify(resolved));
    const parts = [
      `${ASSIGNMENTS_COOKIE}=${value}`,
      'Path=/',
      `Max-Age=${ASSIGNMENTS_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ];
    const domain = sharedCookieDomain();
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  } catch {
    // Best-effort: losing the bridge cookie only costs signup attribution, never
    // the page or the on-page (www.) tagging.
  }
}

